import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { I18nProvider } from "@/contexts/I18nContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { CustomerAuthProvider } from "@/contexts/CustomerAuthContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { RequestsProvider } from "@/contexts/RequestsContext";
import { SiteSettingsProvider, useSiteSettings } from "@/contexts/SiteSettingsContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import RenderErrorBoundary from "@/components/RenderErrorBoundary";
import OptionalFeatureBoundary from "@/components/OptionalFeatureBoundary";
import PwaExperience from "@/components/PwaExperience";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import WelcomeGate from "@/components/WelcomeGate";

import PublicLayout from "@/components/PublicLayout";
import AdminLayout from "@/components/AdminLayout";

import Home from "@/pages/public/Home";
import Explore from "@/pages/public/Explore";
import ListingDetail from "@/pages/public/ListingDetail";
import Favorites from "@/pages/public/Favorites";
import Requests from "@/pages/public/Requests";
import RequestWizard from "@/pages/public/RequestWizard";
import ProviderApply from "@/pages/public/ProviderApply";
import About from "@/pages/public/About";
import Contact from "@/pages/public/Contact";
import Business from "@/pages/public/Business";
import BusinessSubmit from "@/pages/public/BusinessSubmit";
import UniversePortal from "@/pages/public/UniversePortal";
import ServiceDetail from "@/pages/public/ServiceDetail";
import ServiceOrder from "@/pages/public/ServiceOrder";
import TicketingHome, { TicketingEvent, TicketingSubmit, TicketingConfirmation, TicketView } from "@/pages/public/Ticketing";
import MarketplaceHome, { MarketplaceProduct, MarketplaceOrder } from "@/pages/public/Marketplace";
import CustomerAuth from "@/pages/public/CustomerAuth";
import CustomerAccount from "@/pages/public/CustomerAccount";
import LocalSeoLanding from "@/pages/public/LocalSeoLanding";

import AdminLogin from "@/pages/admin/Login";
import Dashboard from "@/pages/admin/Dashboard";
import Leads from "@/pages/admin/Leads";
import OwnerRequests from "@/pages/admin/OwnerRequests";
import AdminBookings from "@/pages/admin/AdminBookings";
import Visits from "@/pages/admin/Visits";
import Invoices from "@/pages/admin/Invoices";
import AdminListings from "@/pages/admin/AdminListings";
import ListingWizardStart from "@/pages/admin/ListingWizardStart";
import QuickListing from "@/pages/admin/QuickListing";
import WizardImmo from "@/pages/admin/WizardImmo";
import WizardAuto from "@/pages/admin/WizardAuto";
import WizardStay from "@/pages/admin/WizardStay";
import WizardBusiness from "@/pages/admin/WizardBusiness";
import Deals from "@/pages/admin/Deals";
import NewDeal from "@/pages/admin/NewDeal";
import DealDetail from "@/pages/admin/DealDetail";
import Settings from "@/pages/admin/Settings";
import Partners from "@/pages/admin/Partners";
import PartnerCatalog from "@/pages/admin/PartnerCatalog";
import AdminServices from "@/pages/admin/AdminServices";
import AdminTicketing from "@/pages/admin/Ticketing";
import AdminMarketplace from "@/pages/admin/Marketplace";
import Tasks from "@/pages/admin/Tasks";
import Documents from "@/pages/admin/Documents";
import Reports from "@/pages/admin/Reports";

function PortalGuard({ setting, any, children }) {
  const { settings } = useSiteSettings();
  const visibility = settings?.portal_visibility || {};
  const enabled = any ? any.some((key) => visibility[key] !== false) : setting === "business_enabled" ? visibility[setting] === true : visibility[setting] !== false;
  return enabled ? children : <Navigate to="/" replace />;
}

function ExplorerGuard({ children }) {
  const { settings } = useSiteSettings();
  const { search } = useLocation();
  const visibility = settings?.portal_visibility || {};
  const params = new URLSearchParams(search);
  const sector = params.get("sector");
  const category = params.get("category");
  const portal = params.get("portal");
  const spaces = ["coworking","bureau_prive","salle_reunion","salle_conference","salle_formation","salle_mariage","salle_reception"];
  const isSpacesPortal = portal === "espaces" || spaces.includes(category);
  const disabled = (sector === "auto" && visibility.auto_enabled === false) ||
    (sector === "hospitality" && visibility.hospitality_enabled === false) ||
    (sector === "real_estate" && isSpacesPortal && visibility.spaces_enabled === false) ||
    (sector === "real_estate" && !isSpacesPortal && visibility.property_enabled === false);
  return disabled ? <Navigate to="/" replace /> : children;
}

function App() {
  return (
    <RenderErrorBoundary>
      <I18nProvider>
        <AuthProvider>
          <CustomerAuthProvider>
            <SiteSettingsProvider>
              <FavoritesProvider>
                <RequestsProvider>
                  <BrowserRouter>
                    <OptionalFeatureBoundary><AnalyticsTracker /></OptionalFeatureBoundary>
                    <OptionalFeatureBoundary><PwaExperience /></OptionalFeatureBoundary>
                    <OptionalFeatureBoundary><WelcomeGate /></OptionalFeatureBoundary>
                    <Toaster position="top-center" richColors closeButton />
                    <Routes>
                      <Route element={<PublicLayout />}>
                        <Route path="/" element={<Home />} />
                        <Route path="/explorer" element={<ExplorerGuard><Explore /></ExplorerGuard>} />
                        <Route path="/univers/:universe" element={<UniversePortal />} />
                        <Route path="/services/commander" element={<PortalGuard setting="services_enabled"><ServiceOrder /></PortalGuard>} />
                        <Route path="/services/:slug" element={<PortalGuard setting="services_enabled"><ServiceDetail /></PortalGuard>} />
                        <Route path="/billetterie" element={<PortalGuard setting="ticketing_enabled"><TicketingHome /></PortalGuard>} />
                        <Route path="/billetterie/soumettre" element={<PortalGuard setting="ticketing_enabled"><TicketingSubmit /></PortalGuard>} />
                        <Route path="/billetterie/confirmation" element={<PortalGuard setting="ticketing_enabled"><TicketingConfirmation /></PortalGuard>} />
                        <Route path="/billetterie/:slug" element={<PortalGuard setting="ticketing_enabled"><TicketingEvent /></PortalGuard>} />
                        <Route path="/billet/:reference" element={<TicketView />} />
                        <Route path="/marketplace" element={<PortalGuard setting="marketplace_enabled"><MarketplaceHome /></PortalGuard>} />
                        <Route path="/marketplace/commande/:reference" element={<PortalGuard setting="marketplace_enabled"><MarketplaceOrder /></PortalGuard>} />
                        <Route path="/marketplace/:slug" element={<PortalGuard setting="marketplace_enabled"><MarketplaceProduct /></PortalGuard>} />
                        <Route path="/devenir-partenaire" element={<ProviderApply />} />
                        <Route path="/immobilier" element={<Navigate to="/univers/immobilier" replace />} />
                        <Route path="/automobile" element={<Navigate to="/univers/automobile" replace />} />
                        <Route path="/sejours" element={<Navigate to="/univers/sejours" replace />} />
                        <Route path="/business" element={<PortalGuard setting="business_enabled"><Business /></PortalGuard>} />
                        <Route path="/business/vendre" element={<PortalGuard setting="business_enabled"><PortalGuard any={["business_sell_enabled","business_fundraise_enabled"]}><BusinessSubmit /></PortalGuard></PortalGuard>} />
                        <Route path="/annonce/:id" element={<ListingDetail />} />
                        <Route path="/connexion" element={<CustomerAuth />} />
                        <Route path="/compte" element={<CustomerAccount />} />
                        <Route path="/favoris" element={<Favorites />} />
                        <Route path="/demandes" element={<Requests />} />
                        <Route path="/demandes/nouvelle" element={<RequestWizard />} />
                        <Route path="/profil" element={<Navigate to="/compte" replace />} />
                        <Route path="/a-propos" element={<About />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/solutions/:slug" element={<LocalSeoLanding />} />
                      </Route>

                      <Route path="/admin/login" element={<AdminLogin />} />
                      <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                        <Route index element={<Dashboard />} />
                        <Route path="leads" element={<Leads />} />
                        <Route path="requests" element={<OwnerRequests />} />
                        <Route path="ticketing" element={<AdminTicketing />} />
                        <Route path="marketplace" element={<AdminMarketplace />} />
                        <Route path="bookings" element={<AdminBookings />} />
                        <Route path="visits" element={<Visits />} />
                        <Route path="invoices" element={<Invoices />} />
                        <Route path="listings" element={<AdminListings />} />
                        <Route path="listings/new" element={<ListingWizardStart />} />
                        <Route path="listings/quick" element={<QuickListing />} />
                        <Route path="listings/immo/new" element={<WizardImmo />} />
                        <Route path="listings/immo/:id" element={<WizardImmo />} />
                        <Route path="listings/auto/new" element={<WizardAuto />} />
                        <Route path="listings/auto/:id" element={<WizardAuto />} />
                        <Route path="listings/stay/new" element={<WizardStay />} />
                        <Route path="listings/stay/:id" element={<WizardStay />} />
                        <Route path="listings/business/new" element={<WizardBusiness />} />
                        <Route path="listings/business/:id" element={<WizardBusiness />} />
                        <Route path="deals" element={<Deals />} />
                        <Route path="deals/new" element={<NewDeal />} />
                        <Route path="deals/:id" element={<DealDetail />} />
                        <Route path="partners" element={<Partners />} />
                        <Route path="catalog" element={<PartnerCatalog />} />
                        <Route path="services" element={<AdminServices />} />
                        <Route path="tasks" element={<Tasks />} />
                        <Route path="documents" element={<Documents />} />
                        <Route path="reports" element={<Reports />} />
                        <Route path="settings" element={<Settings />} />
                      </Route>

                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </BrowserRouter>
                </RequestsProvider>
              </FavoritesProvider>
            </SiteSettingsProvider>
          </CustomerAuthProvider>
        </AuthProvider>
      </I18nProvider>
    </RenderErrorBoundary>
  );
}

export default App;
